import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../../hooks/useBackHandler';
import { getMonsterImgPath } from '../../utils/monster';

/**
 * **추가 방덱** — 공략 하나에 대표 방덱 말고도 붙이는 방덱들(이 공덱으로 함께 뚫는 방덱, 사용자 2026-10-13).
 *
 * 칸을 하나씩 채우는 방식은 버거워서(사용자) **작은 목록**으로 보인다 — 방덱 하나 = 작은 몹 그림 셋. 붙이기는 게시글 수정의
 * [가져오기](통계의 상대 방덱)와 전투기록의 [이 방덱을 공략에 추가]로, 빼기는 수정 화면에서 목록의 ×(또는 [가져오기]에서 체크 해제).
 * 붙인 방덱은 방덱 검색·카운터 목록에서 대표 방덱처럼 이 공략을 띄운다(서버 `withExtraDefenses`).
 */
export type Mob = { name: string; attribute: string };

export default function ExtraDefenseRows({ rows, mapping, size, editable, onRemoveRow }: {
    rows: Mob[][] | undefined;
    mapping: any;
    /** 몹 그림 한 변(px) — 작게 */
    size: number;
    editable: boolean;
    onRemoveRow?: (row: number) => void;
}) {
    const list = (rows || []).filter((r) => r.length === 3 && r.every((m) => m?.name));
    if (!list.length) return null;
    return (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', maxWidth: 360 }}>
            {list.map((row, r) => (
                <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 8, background: '#fff', border: '1px solid rgba(211,47,47,0.25)' }}>
                    {row.map((m, s) => <MobIcon key={s} m={m} mapping={mapping} size={size} />)}
                    {editable && (
                        <button onClick={() => onRemoveRow?.((rows || []).indexOf(row))} title="이 방덱 빼기" aria-label="이 방덱 빼기" style={{
                            border: 'none', background: 'transparent', color: '#d32f2f', fontWeight: 900, fontSize: 14, lineHeight: 1, cursor: 'pointer', padding: '0 3px'
                        }}>×</button>
                    )}
                </div>
            ))}
        </div>
    );
}

type Team = Mob[];
interface PopDef { defenseTeam: Team; totalMatches: number; wins: number; losses: number; winRate: number }
const keyOf = (t: Team) => t.map((m) => `${m.name}|${m.attribute}`).join('/');

function MobIcon({ m, mapping, size }: { m: Mob; mapping: any; size: number }) {
    const { imgPath, displayName } = getMonsterImgPath(mapping, m);
    return imgPath ? (
        <img loading="lazy" src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} title={displayName}
            style={{ width: size, height: size, borderRadius: size * 0.2, objectFit: 'contain', background: '#fff', border: '1.5px solid rgba(211,47,47,0.35)', flexShrink: 0 }} />
    ) : (
        <div title={displayName} style={{ width: size, height: size, borderRadius: size * 0.2, background: '#f2f2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#999', flexShrink: 0 }}>{displayName}</div>
    );
}

/**
 * **[가져오기]** — 통계의 상대 방덱(5성·4성)에서 골라 넣는다(사용자). 방덱 칸 아래에 둔다.
 */
export function ImportDefenseButton({ rows, onChange, main, onMain, mapping, isMobile }: {
    rows: Mob[][]; onChange: (next: Mob[][]) => void;
    /** 대표 방덱 칸 — 비어 있으면 가져온 방덱이 먼저 여기로 들어간다 */
    main?: Mob[]; onMain?: (team: Mob[]) => void;
    mapping: any; isMobile: boolean;
}) {
    const [open, setOpen] = useState(false);
    const isEmpty = (r: Mob[] | undefined) => !r || r.every((m) => !m?.name);
    const isFull = (r: Mob[]) => r.length === 3 && r.every((m) => m.name && m.attribute);
    const full = [...(main && isFull(main) ? [main] : []), ...rows.filter(isFull)];
    /**
     * 고르면 **빈 방덱 칸부터 채운다**(사용자) — 대표 방덱이 비었으면 거기, 아니면 추가 방덱 목록에. 이미 들어 있는(체크된)
     * 방덱을 누르면 뺀다. 누르는 즉시 창을 닫는다.
     */
    const pick = (team: Team) => {
        const t = team.map((m) => ({ name: m.name, attribute: m.attribute }));
        const k = keyOf(t);
        if (full.some((r) => keyOf(r) === k)) {
            // 체크 해제 — 대표 방덱이면 대표 칸을 비우고, 추가 방덱이면 목록에서 뺀다(사용자)
            if (onMain && main && keyOf(main) === k) onMain([{ name: '', attribute: '' }, { name: '', attribute: '' }, { name: '', attribute: '' }]);
            else onChange(rows.filter((r) => keyOf(r) !== k));
        } else if (onMain && isEmpty(main)) {
            onMain(t);
        } else {
            const at = rows.findIndex(isEmpty);
            onChange(at >= 0 ? rows.map((r, i) => (i === at ? t : r)) : [...rows, t]);
        }
        setOpen(false);
    };
    const toggle = pick;
    return (
        <>
            <button onClick={() => setOpen(true)} title="통계의 상대 방덱에서 가져오기" style={{
                alignSelf: 'center', flexShrink: 0, padding: isMobile ? '5px 9px' : '6px 12px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
                border: '1.5px solid #c62828', background: '#fff', color: '#c62828', fontWeight: 800, fontSize: isMobile ? 11 : 12
            }}>가져오기</button>
            {open && <DefensePicker mapping={mapping} isMobile={isMobile} chosen={full} saving={false} onToggle={toggle} onClose={() => setOpen(false)} />}
        </>
    );
}

/** 통계의 상대 방덱에서 고르는 창 — 5성 / 4성, 몹 이름으로 거르기. 누르면 그 방덱을 넣고 닫힌다 */
function DefensePicker({ mapping, isMobile, chosen, saving, onToggle, onClose }: {
    mapping: any; isMobile: boolean; chosen: Team[]; saving: boolean; onToggle: (t: Team) => void; onClose: () => void;
}) {
    useBackHandler(true, onClose);
    const [stars, setStars] = useState<5 | 4>(5);
    const [data, setData] = useState<Record<number, PopDef[] | null>>({ 5: null, 4: null });
    const [q, setQ] = useState('');

    useEffect(() => {
        if (data[stars]) return;
        let gone = false;
        fetch(`/api/posts/popular-defenses?minCount=10&stars=${stars}&side=opp`)
            .then((r) => r.json())
            .then((j) => { if (!gone) setData((d) => ({ ...d, [stars]: j?.success && Array.isArray(j.defenses) ? j.defenses : [] })); })
            .catch(() => { if (!gone) setData((d) => ({ ...d, [stars]: [] })); });
        return () => { gone = true; };
    }, [stars, data]);

    const chosenKeys = new Set(chosen.map(keyOf));
    const rows = (data[stars] || []).filter((d) => {
        if (!q.trim()) return true;
        const s = q.trim().toLowerCase();
        return d.defenseTeam.some((m) => (getMonsterImgPath(mapping, m).displayName || m.name).toLowerCase().includes(s));
    });
    const icon = isMobile ? 36 : 44;

    return (
        createPortal(
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,10,30,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 8 : 24 }}>
                <div onClick={(e) => e.stopPropagation()} style={{
                    width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 18,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: isMobile ? '12px 14px' : '14px 18px', borderBottom: '1px solid #eee' }}>
                        <span style={{ fontWeight: 900, fontSize: isMobile ? 15 : 17, color: '#333' }}>호환 방덱 추가</span>
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#999', fontWeight: 600 }}>통계의 상대 방덱(전투 10회 이상)</span>
                        <button onClick={onClose} aria-label="닫기" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 24, fontWeight: 900, color: '#888', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: isMobile ? '10px 14px' : '12px 18px' }}>
                        {([5, 4] as const).map((s) => (
                            <button key={s} onClick={() => setStars(s)} style={{
                                padding: '7px 16px', borderRadius: 999, border: stars === s ? 'none' : '1px solid #ddd', cursor: 'pointer', fontWeight: 800, fontSize: 13,
                                background: stars === s ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff', color: stars === s ? '#fff' : '#666'
                            }}>{s}성</button>
                        ))}
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="몹 이름으로 찾기"
                            style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e0e0e0', fontSize: 13, outline: 'none' }} />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0 10px 12px' : '0 18px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {!data[stars] ? <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>방덱을 불러오는 중…</div>
                            : rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>맞는 방덱이 없습니다</div>
                                : rows.map((d) => {
                                    const on = chosenKeys.has(keyOf(d.defenseTeam));
                                    return (
                                        <button key={keyOf(d.defenseTeam)} onClick={() => onToggle(d.defenseTeam)} disabled={saving} style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                                            border: on ? '2px solid #667eea' : '1px solid #eee', background: on ? '#eef2ff' : '#fff'
                                        }}>
                                            <div style={{ display: 'flex', gap: 4 }}>{d.defenseTeam.map((m, i) => <MobIcon key={i} m={m} mapping={mapping} size={icon} />)}</div>
                                            <div style={{ fontSize: isMobile ? 11 : 12, color: '#666', fontWeight: 700, lineHeight: 1.5 }}>
                                                <div>{d.totalMatches}회</div>
                                                <div style={{ color: '#667eea' }}>방성 {(100 - d.winRate).toFixed(1)}%</div>
                                            </div>
                                            {on && <span style={{ marginLeft: 'auto', fontWeight: 900, fontSize: 15, color: '#667eea' }}>✓</span>}
                                        </button>
                                    );
                                })}
                    </div>
                </div>
            </div>,
        document.body
        )
    );
}

const sameTeam = (a: Mob[] | undefined, b: Mob[] | undefined) => {
    const id = (m: Mob) => `${m?.name}|${m?.attribute}`;
    return !!a && !!b && a.length === 3 && b.length === 3 && id(a[0]) === id(b[0]) && [id(a[1]), id(a[2])].sort().join('/') === [id(b[1]), id(b[2])].sort().join('/');
};

/**
 * **[기존 공략에 호환 방덱으로 추가]** — 방덱 검색 결과의 [공략 작성하러 가기] 옆 길(사용자 2026-10-13). 새 공략을 쓰는 대신 이 방덱을
 * 이미 있는 공략의 호환 방덱으로 붙인다. 누르면 공략 목록 창이 뜨고, 고르면 바로 덧붙이고 닫는다
 * (`POST /api/tactics/:id/extra-defenses/add`). **운영진만** — 화면은 운영진에게만 보이고 서버도 막는다.
 */
export function AttachToTacticCard({ defenseTeam, mapping, isMobile, onDone, compact }: {
    defenseTeam: Mob[]; mapping: any; isMobile: boolean; onDone?: () => void;
    /** 버튼 모양(검색 결과가 텅 빈 자리) — 아니면 [공략 작성하러 가기] 와 같은 줄 카드 */
    compact?: boolean;
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            {compact ? (
                <button onClick={() => setOpen(true)} style={{
                    marginTop: 8, border: '1.5px solid #c62828', borderRadius: 10, padding: isMobile ? '10px 16px' : '12px 20px', background: '#fff',
                    color: '#c62828', fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: 'pointer'
                }}>기존 공략에 호환 방덱으로 추가</button>
            ) : (
                <div onClick={() => setOpen(true)} style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, padding: isMobile ? '10px 12px' : '12px 14px', marginBottom: 12,
                    borderRadius: 12, cursor: 'pointer', border: '2px dashed rgba(198, 40, 40, 0.4)', background: 'rgba(198, 40, 40, 0.04)'
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #ef5350 0%, #c62828 100%)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 8px',
                        borderRadius: 6, whiteSpace: 'nowrap', letterSpacing: 0.5
                    }}>호환</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: '#c62828' }}>기존 공략에 호환 방덱으로 추가</div>
                        <div style={{ fontSize: isMobile ? 11 : 12, color: '#999', marginTop: 2 }}>새로 쓰지 않고 이 방덱을 다른 공략에 붙입니다</div>
                    </div>
                    <div style={{ fontSize: 20, color: '#c62828', fontWeight: 700, flexShrink: 0 }}>+</div>
                </div>
            )}
            {open && <TacticPicker defenseTeam={defenseTeam} mapping={mapping} isMobile={isMobile} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
        </>
    );
}

/** 공략 고르는 창 — 줄을 누르면 이 방덱을 그 공략에 붙이고 닫는다. 이미 들어 있으면 ✓ */
function TacticPicker({ defenseTeam, mapping, isMobile, onClose, onDone }: {
    defenseTeam: Mob[]; mapping: any; isMobile: boolean; onClose: () => void; onDone: () => void;
}) {
    useBackHandler(true, onClose);
    const [list, setList] = useState<any[] | null>(null);
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    useEffect(() => {
        let gone = false;
        fetch('/api/tactics', { credentials: 'include' })
            .then((r) => r.json())
            .then((all) => { if (!gone) setList(Array.isArray(all) ? all : []); })
            .catch(() => { if (!gone) setList([]); });
        return () => { gone = true; };
    }, []);
    const has = (t: any) => sameTeam(t.defenseTeam, defenseTeam) || (t.extraDefenses || []).some((d: Mob[]) => sameTeam(d, defenseTeam));
    const rows = list || [];
    const add = async (t: any) => {
        if (has(t) || busy) return;
        setBusy(t._id); setErr('');
        try {
            const r = await fetch(`/api/tactics/${t._id}/extra-defenses/add`, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ team: defenseTeam.map((m) => ({ name: m.name, attribute: m.attribute })) })
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.success) throw new Error(j.error || '추가하지 못했습니다');
            onDone();
        } catch (e: any) {
            setErr(e.message || '추가하지 못했습니다');
        } finally {
            setBusy('');
        }
    };
    const icon = isMobile ? 28 : 32;

    return (
        createPortal(
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,10,30,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 8 : 24 }}>
                <div onClick={(e) => e.stopPropagation()} style={{
                    width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 18,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? '12px 14px' : '14px 18px', borderBottom: '1px solid #eee' }}>
                        <span style={{ fontWeight: 900, fontSize: isMobile ? 15 : 17, color: '#5a4fcf', whiteSpace: 'nowrap' }}>방덱</span>
                        <div style={{ display: 'flex', gap: 2 }}>{defenseTeam.map((m, i) => <MobIcon key={i} m={m} mapping={mapping} size={isMobile ? 22 : 26} />)}</div>
                        <button onClick={onClose} aria-label="닫기" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 24, fontWeight: 900, color: '#888', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                    {err && <div style={{ padding: isMobile ? '8px 14px 0' : '10px 18px 0', color: '#e11d48', fontSize: 12, fontWeight: 700 }}>{err}</div>}
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '10px 10px 12px' : '12px 18px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {!list ? <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>공략을 불러오는 중…</div>
                            : rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>공략이 없습니다</div>
                                : rows.map((t) => {
                                    const already = has(t);
                                    return (
                                        <div key={t._id} role="button" onClick={() => add(t)} style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderRadius: 12, textAlign: 'left',
                                            cursor: already ? 'default' : 'pointer', opacity: busy === t._id ? 0.6 : 1,
                                            border: already ? '2px solid #e57373' : '1px solid #eee', background: already ? '#fdeeee' : '#fff'
                                        }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontWeight: 800, fontSize: isMobile ? 12 : 13, color: '#5a4fcf', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 4, flexWrap: 'wrap' }}>
                                                    {(t.attackTeam || []).filter((m: Mob) => m?.name).map((m: Mob, i: number) => (
                                                        <span key={i} style={{ display: 'inline-flex', borderRadius: icon * 0.22, outline: '1.5px solid rgba(25,118,210,0.5)' }}><MobIcon m={m} mapping={mapping} size={icon} /></span>
                                                    ))}
                                                    {/* 대표 방덱이 없으면 vs 도 적지 않는다(사용자) */}
                                                    {(t.defenseTeam || []).length === 3 && (t.defenseTeam as Mob[]).every((m) => m?.name) && (
                                                        <>
                                                            <span style={{ margin: '0 4px', fontSize: 11, fontWeight: 800, color: '#bbb' }}>vs</span>
                                                            {(t.defenseTeam as Mob[]).map((m, i) => <MobIcon key={i} m={m} mapping={mapping} size={icon} />)}
                                                        </>
                                                    )}
                                                    {(t.extraDefenses || []).length > 0 && <span style={{ marginLeft: 4, fontSize: 11, color: '#c62828', fontWeight: 700 }}>+{(t.extraDefenses || []).length}</span>}
                                                </div>
                                            </div>
                                            {already && <span style={{ flexShrink: 0, fontSize: 12, color: '#c62828', fontWeight: 800 }}>✓ 들어 있음</span>}
                                        </div>
                                    );
                                })}
                    </div>
                </div>
            </div>,
        document.body
        )
    );
}
